import React, { FC } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import CardSkeleton from '@v2/elements/Skeleton/CardSkeleton';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { TRACEABILITY_GRAPH_GEOMETRY } from '@v2/pages/common/components/TraceabilityGraph/constants';
import { TraceabilityColumn, TraceabilityGraphProps } from '@v2/pages/common/components/TraceabilityGraph/types';

interface TraceabilityGraphSkeletonProps extends Pick<TraceabilityGraphProps, 'C'> {
  columns: TraceabilityColumn[];
}

// Loading state for the graph: keeps the static column headers and drops a single card
// placeholder into every column's card area, so the layout looks like it's filling in.
export const TraceabilityGraphSkeleton: FC<TraceabilityGraphSkeletonProps> = ({ C, columns }) => {
  const columnWidth = scale(TRACEABILITY_GRAPH_GEOMETRY.columnWidth);
  const columnGap = scale(TRACEABILITY_GRAPH_GEOMETRY.columnGap);
  const contentWidth = columns.length * columnWidth + Math.max(0, columns.length - 1) * columnGap;

  return (
    <View testID={TEST_IDS.traceabilityGraph.loading} style={{ flex: 1, alignSelf: 'stretch', minHeight: 0 }}>
      <ScrollView style={{ flex: 1, alignSelf: 'stretch' }} contentContainerStyle={{ flexGrow: 1 }}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator
          contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'flex-start', paddingHorizontal: scale(20) }}
        >
          <View style={{ width: contentWidth, paddingVertical: scale(12) }}>
            <View style={{ flexDirection: 'row', gap: columnGap, marginBottom: scale(14) }}>
              {columns.map((column) => (
                <View key={column.id} style={{ width: columnWidth }}>
                  <Text numberOfLines={1} style={[font['Subhead 2'], { color: C('Label/Light/Secondary'), textAlign: 'center' }]}>
                    {column.title}
                  </Text>
                </View>
              ))}
            </View>

            <View style={{ flexDirection: 'row', gap: columnGap }}>
              {columns.map((column) => (
                <View key={column.id} style={{ width: columnWidth }}>
                  <CardSkeleton C={C} height={scale(TRACEABILITY_GRAPH_GEOMETRY.cardMinHeight)} />
                </View>
              ))}
            </View>
          </View>
        </ScrollView>
      </ScrollView>
    </View>
  );
};
